import { ref, set, get, update, onValue, off } from 'firebase/database';
import { database } from './core';
import { SettingsState } from '../../types/settings';
import logger from '../../utils/logger';

const settingsLogger = logger.module('SettingsService');

/**
 * Settings service for Firebase
 * Handles user settings persistence in Firebase Realtime Database
 */
class SettingsService {
  private readonly settingsPath = 'users/:userId/settings';

  /**
   * Get the settings path for a specific user
   */
  private getUserSettingsPath(userId: string): string {
    return this.settingsPath.replace(':userId', userId);
  }

  /**
   * Fetch user settings from Firebase
   */
  async fetchUserSettings(userId: string): Promise<Partial<SettingsState> | null> {
    try {
      const settingsRef = ref(database, this.getUserSettingsPath(userId));
      const snapshot = await get(settingsRef);
      
      if (snapshot.exists()) {
        const settings = snapshot.val();
        settingsLogger.info('Settings fetched successfully', { userId });
        return settings;
      } else {
        settingsLogger.info('No settings found for user', { userId });
        return null;
      }
    } catch (error) {
      settingsLogger.error('Error fetching user settings:', error);
      throw error;
    }
  }

  /**
   * Update user settings in Firebase
   * This performs a partial update, only updating the provided fields
   */
  async updateUserSettings(userId: string, settings: any): Promise<void> {
    try {
      const settingsRef = ref(database, this.getUserSettingsPath(userId));
      
      // Add timestamp
      const settingsWithTimestamp = {
        ...settings,
        lastUpdated: Date.now()
      };
      
      // Use update for partial updates
      await update(settingsRef, settingsWithTimestamp);
      
      settingsLogger.info('Settings updated successfully', { userId });
    } catch (error) {
      settingsLogger.error('Error updating user settings:', error);
      throw error;
    }
  }

  /**
   * Initialize user settings with defaults
   * This is called when a new user is created
   */
  async initializeUserSettings(userId: string, defaultSettings: SettingsState): Promise<void> {
    try {
      const settingsRef = ref(database, this.getUserSettingsPath(userId));
      
      // Check if settings already exist
      const snapshot = await get(settingsRef);
      if (snapshot.exists()) {
        settingsLogger.info('Settings already exist for user', { userId });
        return;
      }
      
      // Set initial settings
      await set(settingsRef, {
        ...defaultSettings,
        lastUpdated: Date.now()
      });
      
      settingsLogger.info('Settings initialized successfully', { userId });
    } catch (error) {
      settingsLogger.error('Error initializing user settings:', error);
      throw error;
    }
  }

  /**
   * Update a specific settings section
   * This is more efficient than updating the entire settings object
   */
  async updateSettingsSection(
    userId: string, 
    section: keyof SettingsState, 
    data: any
  ): Promise<void> {
    try {
      const sectionRef = ref(database, `${this.getUserSettingsPath(userId)}/${section}`);
      
      await set(sectionRef, data);
      
      // Update the lastUpdated timestamp
      const timestampRef = ref(database, `${this.getUserSettingsPath(userId)}/lastUpdated`);
      await set(timestampRef, Date.now());
      
      settingsLogger.info('Settings section updated successfully', { userId, section });
    } catch (error) {
      settingsLogger.error('Error updating settings section:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time settings updates
   * Returns an unsubscribe function
   */
  subscribeToSettings(
    userId: string, 
    callback: (settings: Partial<SettingsState> | null) => void
  ): () => void {
    const settingsRef = ref(database, this.getUserSettingsPath(userId));
    
    const listener = onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val());
      } else {
        callback(null);
      }
    }, (error) => {
      settingsLogger.error('Error in settings subscription:', error);
    });
    
    // Return unsubscribe function
    return () => {
      off(settingsRef, 'value', listener);
    };
  }

  /**
   * Delete user settings
   * This is typically called when deleting a user account
   */
  async deleteUserSettings(userId: string): Promise<void> {
    try {
      const settingsRef = ref(database, this.getUserSettingsPath(userId));
      await set(settingsRef, null);
      
      settingsLogger.info('Settings deleted successfully', { userId });
    } catch (error) {
      settingsLogger.error('Error deleting user settings:', error);
      throw error;
    }
  }

  /**
   * Batch update multiple settings at once
   * This is useful for importing/exporting settings
   */
  async batchUpdateSettings(updates: Record<string, any>): Promise<void> {
    try {
      // Prepare updates with proper paths
      const firebaseUpdates: Record<string, any> = {};
      
      Object.entries(updates).forEach(([path, value]) => {
        firebaseUpdates[path] = value;
      });
      
      // Add timestamps to each user's settings
      Object.keys(updates).forEach(path => {
        if (path.includes('/settings') && !path.includes('/lastUpdated')) {
          const timestampPath = path.replace('/settings', '/settings/lastUpdated');
          firebaseUpdates[timestampPath] = Date.now();
        }
      });
      
      // Perform batch update
      await update(ref(database), firebaseUpdates);
      
      settingsLogger.info('Batch settings update successful');
    } catch (error) {
      settingsLogger.error('Error in batch settings update:', error);
      throw error;
    }
  }
}

// Export singleton instance
const settingsService = new SettingsService();
export default settingsService;